import { LICENSE_CATEGORIES, LICENSE_TYPES } from './constants';

export interface LicenseRequirement {
  category: string;
  type: 'NEW' | 'RENEWAL';
  requiredDocuments: {
    id: string;
    label: string;
    required: boolean;
    helpText?: string;
  }[];
  requiredFields: {
    section: string;
    fields: {
      id: string;
      label: string;
      type: 'text' | 'date' | 'select' | 'textarea' | 'number';
      required: boolean;
      options?: { value: string; label: string }[];
      helpText?: string;
    }[];
  }[];
}

// Common documents for all NEW applications
const commonNewDocuments = [
  { id: 'passport_photo', label: 'Passport Photograph', required: true, helpText: 'Recent color passport photograph' },
  { id: 'nin_doc', label: 'National Identity Number (NIN) Document', required: true, helpText: 'NIN slip or card' },
  { id: 'birth_certificate', label: 'Birth Certificate', required: true },
  { id: 'medical_certificate', label: 'Medical Certificate', required: true, helpText: 'Valid medical certificate from NCAA approved examiner' },
];

// Common documents for RENEWAL applications
const commonRenewalDocuments = [
  { id: 'existing_license', label: 'Current/Expired License Copy', required: true, helpText: 'Copy of license to be renewed' },
  { id: 'passport_photo', label: 'Recent Passport Photograph', required: true },
  { id: 'medical_certificate', label: 'Updated Medical Certificate', required: true, helpText: 'Valid medical certificate from NCAA approved examiner' },
];

// Common personal information fields
const personalInfoFields = [
  { id: 'surname', label: 'Surname', type: 'text' as const, required: true },
  { id: 'middle_name', label: 'Middle Name', type: 'text' as const, required: false },
  { id: 'first_name', label: 'First Name', type: 'text' as const, required: true },
  { id: 'dob', label: 'Date of Birth', type: 'date' as const, required: true },
  { id: 'place_of_birth', label: 'Place of Birth', type: 'text' as const, required: true },
  { id: 'nationality', label: 'Nationality', type: 'text' as const, required: true },
  { id: 'address', label: 'Residential Address', type: 'textarea' as const, required: true },
];

// Medical certificate fields
const medicalFields = [
  { id: 'medical_class', label: 'Medical Certificate Class', type: 'select' as const, required: true, 
    options: [
      { value: 'CLASS_1', label: 'Class 1' },
      { value: 'CLASS_2', label: 'Class 2' },
      { value: 'CLASS_3', label: 'Class 3' },
    ]
  },
  { id: 'height', label: 'Height (cm)', type: 'number' as const, required: true },
  { id: 'weight', label: 'Weight (kg)', type: 'number' as const, required: true },
  { id: 'hair_color', label: 'Color of Hair', type: 'text' as const, required: true },
  { id: 'eye_color', label: 'Color of Eyes', type: 'text' as const, required: true },
  { id: 'exam_date', label: 'Date of Medical Examination', type: 'date' as const, required: true },
  { id: 'medical_valid_till', label: 'Certificate Valid Till', type: 'date' as const, required: true },
  { id: 'ame_name', label: 'Name of Authorized Aviation Medical Examiner (AAME)', type: 'text' as const, required: true },
];

// Renewal-specific fields
const renewalFields = [
  { id: 'license_number', label: 'Current License Number', type: 'text' as const, required: true },
  { id: 'license_issue_date', label: 'Date of Issue', type: 'date' as const, required: true },
  { id: 'license_expiry_date', label: 'License Expiry Date', type: 'date' as const, required: true },
];

export const LICENSE_REQUIREMENTS: LicenseRequirement[] = [
  // ==================== PILOT LICENSE ====================
  {
    category: LICENSE_CATEGORIES.PILOT,
    type: LICENSE_TYPES.NEW,
    requiredDocuments: [
      ...commonNewDocuments,
      { id: 'training_certificate', label: 'Flight Training Certificate', required: true },
      { id: 'logbook', label: 'Pilot Logbook', required: true, helpText: 'Certified copy of logbook showing flight hours' },
      { id: 'knowledge_test', label: 'Knowledge Test Certificate', required: true },
      { id: 'skill_test', label: 'Skill Test Certificate', required: true },
      { id: 'radio_telephony', label: 'Radio Telephony License', required: true },
    ],
    requiredFields: [
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Aircraft Ratings', fields: [
        { id: 'category_rating', label: 'Category Rating', type: 'select' as const, required: true,
          options: [
            { value: 'AIRPLANE', label: 'Airplane' },
            { value: 'HELICOPTER', label: 'Helicopter' },
            { value: 'GLIDER', label: 'Glider' },
          ]
        },
        { id: 'class_rating', label: 'Class Rating', type: 'text' as const, required: false },
        { id: 'type_rating', label: 'Type Rating', type: 'text' as const, required: false },
      ]},
      { section: 'Medical Certificate', fields: medicalFields },
    ],
  },
  {
    category: LICENSE_CATEGORIES.PILOT,
    type: LICENSE_TYPES.RENEWAL,
    requiredDocuments: [
      ...commonRenewalDocuments,
      { id: 'logbook_update', label: 'Updated Logbook', required: true, helpText: 'Recent flight activity' },
      { id: 'proficiency_check', label: 'Proficiency Check Certificate', required: true },
      { id: 'radio_telephony', label: 'Valid Radio Telephony License', required: true },
    ],
    requiredFields: [
      { section: 'License Information', fields: renewalFields },
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Ratings & Endorsements', fields: [
        { id: 'current_ratings', label: 'Current Ratings', type: 'textarea' as const, required: true },
        { id: 'instructor_rating_valid', label: 'Instructor Rating Valid Until', type: 'date' as const, required: false },
        { id: 'instrument_test_valid', label: 'Instrument Test Valid Until', type: 'date' as const, required: false },
      ]},
      { section: 'Medical Certificate', fields: medicalFields },
    ],
  },

  // ==================== CABIN CREW LICENSE ====================
  {
    category: LICENSE_CATEGORIES.CABIN_CREW,
    type: LICENSE_TYPES.NEW,
    requiredDocuments: [
      ...commonNewDocuments,
      { id: 'training_certificate', label: 'Cabin Crew Training Certificate', required: true },
      { id: 'emergency_drill', label: 'Emergency Drill Certificate', required: true },
      { id: 'first_aid', label: 'First Aid Certificate', required: true },
    ],
    requiredFields: [
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Medical Certificate', fields: medicalFields },
      { section: 'Emergency Procedures', fields: [
        { id: 'emergency_drill_date', label: 'Emergency Drill Date', type: 'date' as const, required: true },
        { id: 'emergency_drill_due', label: 'Emergency Drill Due Date', type: 'date' as const, required: true },
      ]},
    ],
  },
  {
    category: LICENSE_CATEGORIES.CABIN_CREW,
    type: LICENSE_TYPES.RENEWAL,
    requiredDocuments: [
      ...commonRenewalDocuments,
      { id: 'emergency_drill', label: 'Updated Emergency Drill Certificate', required: true },
      { id: 'recurrent_training', label: 'Recurrent Training Certificate', required: true },
    ],
    requiredFields: [
      { section: 'License Information', fields: renewalFields },
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Emergency Procedures', fields: [
        { id: 'last_emergency_drill', label: 'Last Emergency Drill Type', type: 'text' as const, required: true },
        { id: 'emergency_drill_date', label: 'Emergency Drill Date', type: 'date' as const, required: true },
        { id: 'emergency_drill_due', label: 'Emergency Drill Due Date', type: 'date' as const, required: true },
      ]},
      { section: 'Medical Certificate', fields: medicalFields },
    ],
  },

  // ==================== FLIGHT DISPATCHER LICENSE ====================
  {
    category: LICENSE_CATEGORIES.FLIGHT_DISPATCHER,
    type: LICENSE_TYPES.NEW,
    requiredDocuments: [
      ...commonNewDocuments,
      { id: 'training_certificate', label: 'Flight Dispatcher Training Certificate', required: true },
      { id: 'knowledge_test', label: 'Knowledge Test Certificate', required: true },
      { id: 'competency_cert', label: 'Competency Certificate', required: true },
    ],
    requiredFields: [
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Training & Competency', fields: [
        { id: 'training_completion', label: 'Training Completion Date', type: 'date' as const, required: true },
        { id: 'competency_date', label: 'Competency Check Date', type: 'date' as const, required: true },
      ]},
    ],
  },
  {
    category: LICENSE_CATEGORIES.FLIGHT_DISPATCHER,
    type: LICENSE_TYPES.RENEWAL,
    requiredDocuments: [
      ...commonRenewalDocuments,
      { id: 'recurrency_cert', label: 'Recurrency Certificate', required: true },
      { id: 'competency_cert', label: 'Updated Competency Certificate', required: true },
    ],
    requiredFields: [
      { section: 'License Information', fields: renewalFields },
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Recurrency & Competency', fields: [
        { id: 'last_recurrency_date', label: 'Date of Last Recurrency', type: 'date' as const, required: true },
        { id: 'recurrency_valid_till', label: 'Recurrency Valid Till', type: 'date' as const, required: true },
        { id: 'last_competency_date', label: 'Date of Last Competency', type: 'date' as const, required: true },
        { id: 'competency_valid_till', label: 'Competency Valid Till', type: 'date' as const, required: true },
      ]},
    ],
  },

  // ==================== AME LICENSE ====================
  {
    category: LICENSE_CATEGORIES.AME,
    type: LICENSE_TYPES.NEW,
    requiredDocuments: [
      ...commonNewDocuments,
      { id: 'training_certificate', label: 'AME Training Certificate', required: true },
      { id: 'knowledge_test', label: 'Knowledge Test Certificate', required: true },
      { id: 'practical_test', label: 'Practical Test Certificate', required: true },
    ],
    requiredFields: [
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Ratings & Categories', fields: [
        { id: 'ratings', label: 'Ratings', type: 'textarea' as const, required: true, helpText: 'Specify all ratings' },
        { id: 'category', label: 'Category', type: 'text' as const, required: true },
        { id: 'type_rating', label: 'Type Rating', type: 'textarea' as const, required: false },
      ]},
    ],
  },
  {
    category: LICENSE_CATEGORIES.AME,
    type: LICENSE_TYPES.RENEWAL,
    requiredDocuments: [
      ...commonRenewalDocuments,
      { id: 'competency_cert', label: 'Competency Certificate', required: true },
      { id: 'continued_training', label: 'Continued Training Evidence', required: true },
    ],
    requiredFields: [
      { section: 'License Information', fields: renewalFields },
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Ratings & Categories', fields: [
        { id: 'current_ratings', label: 'Current Ratings', type: 'textarea' as const, required: true },
        { id: 'current_category', label: 'Current Category', type: 'text' as const, required: true },
        { id: 'type_ratings', label: 'Type Ratings', type: 'textarea' as const, required: false },
      ]},
    ],
  },

  // ==================== ATC LICENSE ====================
  {
    category: LICENSE_CATEGORIES.ATC,
    type: LICENSE_TYPES.NEW,
    requiredDocuments: [
      ...commonNewDocuments,
      { id: 'training_certificate', label: 'ATC Training Certificate', required: true },
      { id: 'knowledge_test', label: 'Knowledge Test Certificate', required: true },
      { id: 'proficiency_check', label: 'Proficiency Check Certificate', required: true },
      { id: 'english_test', label: 'English Language Proficiency Test', required: true },
    ],
    requiredFields: [
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Medical Certificate', fields: medicalFields },
      { section: 'Ratings & Language', fields: [
        { id: 'ratings', label: 'Ratings', type: 'textarea' as const, required: false, helpText: 'e.g., Tower, Approach, Area Control' },
        { id: 'english_level', label: 'English Language Proficiency Level', type: 'select' as const, required: true,
          options: [
            { value: '4', label: 'Level 4 - Operational' },
            { value: '5', label: 'Level 5 - Extended' },
            { value: '6', label: 'Level 6 - Expert' },
          ]
        },
      ]},
    ],
  },
  {
    category: LICENSE_CATEGORIES.ATC,
    type: LICENSE_TYPES.RENEWAL,
    requiredDocuments: [
      ...commonRenewalDocuments,
      { id: 'proficiency_check', label: 'Proficiency Check Certificate', required: true },
      { id: 'english_test', label: 'English Language Proficiency Test (if due)', required: false },
    ],
    requiredFields: [
      { section: 'License Information', fields: renewalFields },
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Medical Certificate', fields: medicalFields },
      { section: 'Ratings & Proficiency', fields: [
        { id: 'current_ratings', label: 'Current Ratings', type: 'textarea' as const, required: true },
        { id: 'proficiency_valid_till', label: 'Proficiency Check Valid Until', type: 'date' as const, required: true },
        { id: 'english_level', label: 'English Language Proficiency Level', type: 'select' as const, required: true,
          options: [
            { value: '4', label: 'Level 4 - Operational' },
            { value: '5', label: 'Level 5 - Extended' },
            { value: '6', label: 'Level 6 - Expert' },
          ]
        },
        { id: 'english_valid_till', label: 'English Proficiency Valid Till', type: 'date' as const, required: true },
      ]},
    ],
  },

  // ==================== ATSEP LICENSE ====================
  {
    category: LICENSE_CATEGORIES.ATSEP,
    type: LICENSE_TYPES.NEW,
    requiredDocuments: [
      ...commonNewDocuments,
      { id: 'training_certificate', label: 'ATSEP Training Certificate', required: true },
      { id: 'knowledge_test', label: 'Knowledge Test Certificate', required: true },
      { id: 'practical_test', label: 'Practical Assessment Certificate', required: true },
    ],
    requiredFields: [
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Category & Ratings', fields: [
        { id: 'category', label: 'Category', type: 'select' as const, required: true,
          options: [
            { value: 'CAT_A', label: 'Category A - Communication Systems' },
            { value: 'CAT_B', label: 'Category B - Radio Navigation Aids' },
            { value: 'CAT_C', label: 'Category C - Surveillance Systems' },
            { value: 'CAT_D', label: 'Category D - Airfield Lighting and Visual Landing Systems' },
          ]
        },
        { id: 'ratings', label: 'Ratings', type: 'textarea' as const, required: false },
      ]},
    ],
  },
  {
    category: LICENSE_CATEGORIES.ATSEP,
    type: LICENSE_TYPES.RENEWAL,
    requiredDocuments: [
      ...commonRenewalDocuments,
      { id: 'competency_cert', label: 'Competency Certificate', required: true },
      { id: 'continued_training', label: 'Continued Training Certificate', required: true },
    ],
    requiredFields: [
      { section: 'License Information', fields: renewalFields },
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Category & Ratings', fields: [
        { id: 'current_category', label: 'Current Category', type: 'select' as const, required: true,
          options: [
            { value: 'CAT_A', label: 'Category A - Communication Systems' },
            { value: 'CAT_B', label: 'Category B - Radio Navigation Aids' },
            { value: 'CAT_C', label: 'Category C - Surveillance Systems' },
            { value: 'CAT_D', label: 'Category D - Airfield Lighting and Visual Landing Systems' },
          ]
        },
        { id: 'current_ratings', label: 'Current Ratings', type: 'textarea' as const, required: false },
      ]},
    ],
  },

  // ==================== ASO LICENSE ====================
  {
    category: LICENSE_CATEGORIES.ASO,
    type: LICENSE_TYPES.NEW,
    requiredDocuments: [
      ...commonNewDocuments,
      { id: 'training_certificate', label: 'ASO Training Certificate', required: true },
      { id: 'knowledge_test', label: 'Knowledge Test Certificate', required: true },
      { id: 'practical_test', label: 'Practical Test Certificate', required: true },
    ],
    requiredFields: [
      { section: 'Personal Information', fields: personalInfoFields },
      { section: 'Training', fields: [
        { id: 'training_completion', label: 'Training Completion Date', type: 'date' as const, required: true },
      ]},
    ],
  },
  {
    category: LICENSE_CATEGORIES.ASO,
    type: LICENSE_TYPES.RENEWAL,
    requiredDocuments: [
      ...commonRenewalDocuments,
      { id: 'competency_cert', label: 'Competency Certificate', required: true },
    ],
    requiredFields: [
      { section: 'License Information', fields: renewalFields },
      { section: 'Personal Information', fields: personalInfoFields },
    ],
  },
];

// Helper function to get requirements for specific license category and type
export const getLicenseRequirements = (category: string, type: 'NEW' | 'RENEWAL'): LicenseRequirement | undefined => {
  return LICENSE_REQUIREMENTS.find(req => req.category === category && req.type === type);
};
